import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Space, Typography, Divider, notification, Spin, Empty, theme, Modal, Form } from "antd";
import { ClockCircleOutlined, WarningFilled } from "@ant-design/icons";
import moment from "moment-timezone";
import { useAuth } from "services/Auth";
import { cropStaffName } from "utils/helper";
import {
  deleteCountedStocksFromStoreByContext,
  deleteMovementsFromStoreByContext,
  getCountedStocksFromStoreByContext,
  getMovementsFromStoreByContext,
  getStoredNewStocksByContext,
  offlineHandler,
  terminateService,
  updateMoneyBox,
  updateStocksStore,
} from "utils/offlineCache";
import PageLayoutWrapper from "components/PageLayoutWrapper";
import StartStopService from "./StartStopService";
import SelectedServiceActionsCard from "./Components/SelectedServiceActionsCard";
import SelectedServiceInfoCard from "./Components/SelectedServiceInfoCard";
import { isNilEmptyOrEmptyObject } from "@salespark/toolkit";
import InputMobileNumber from "components/InputMobileNumber";
import dayjs from "dayjs";
import useSelectedService from "hooks/useSelectedService";
import { locationTracker } from "utils/location/geolocation";
import { LOCATION_ACTIONS } from "constants/location";

const { Text } = Typography;

const isMissingTokenQueueError = (response) => {
  return (
    response?.status === false &&
    String(response?.data || "")
      .toLowerCase()
      .includes("staff token is required")
  );
};

const showMissingTokenQueueNotification = () => {
  notification.error({
    message: "Ação bloqueada",
    description: "Não foi possível guardar a ação offline porque falta o token do staff. Faz login online e tenta novamente.",
  });
};

const Services = () => {
  const { token } = theme.useToken();
  const { auth } = useAuth();
  const apiClient = offlineHandler(auth.data);
  const [form] = Form.useForm();
  const [equipmentForm] = Form.useForm();
  const [serviceStatusModal, setServiceStatusModal] = useState({ visible: false, data: null, status: null }); //status: null | "running" | "completed"
  const [embarkationModal, setEmbarkationModal] = useState(false);
  const [equipmentModal, setEquipmentModal] = useState({ visible: false, equipments: null });
  const [data, setData] = useState([]);
  const { selectedService, setSelectedService, clearSelectedService } = useSelectedService();
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getMany("/services");
      if (!response.status) {
        notification.error({
          message: "Erro",
          description: "Erro ao carregar os serviços",
        });
        setLoading(false);
        await apiClient.post("/error-capture", {
          error: response.data,
          action: "/Services/loadData/ getMany /services",
          timestamp: new Date().toISOString(),
        });
        return;
      }
      const rawData = response.data || [];
      // filter services with start_date greater than now - 4 hours and less than now + 1 hours
      const now = moment();
      const filteredData = rawData.filter((service) => {
        const startDate = moment(service.final_start_date);
        return startDate.isAfter(now.clone().subtract(4, "hours")) && startDate.isBefore(now.clone().add(1, "hours"));
      });
      const userServices = filteredData.filter((service) => {
        if (!Array.isArray(service?.staff)) return false;
        return service.staff.some((staff) => {
          if (typeof staff === "string") return staff === auth.data._id;
          return staff?._id === auth.data._id;
        });
      });

      setData(userServices);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      notification.error({
        message: "Erro",
        description: "Erro ao carregar os serviços",
      });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/Services/loadData/catch",
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleServiceSelect = (service) => {
    if (selectedService?.serviceStatus === "running") return;
    let serviceData = service;
    if (service.equipment?.length > 1) {
      setEquipmentModal({ visible: true, equipments: service.equipment });
      setSelectedService(serviceData);
      return;
    }
    setSelectedService(serviceData);
    deleteMovementsFromStoreByContext({
      user_id: auth?.data?._id || null,
      service_id_raw: serviceData?.service_id_raw || null,
      equipment: serviceData?.equipment?.[0]?._id || null,
    });
    deleteCountedStocksFromStoreByContext({
      user_id: auth?.data?._id || null,
      service_id_raw: serviceData?.service_id_raw || null,
      equipment: serviceData?.equipment?.[0]?._id || null,
    });
  };

  const handleStartStopService = (action) => {
    if (action === "start") setServiceStatusModal({ visible: true, data: selectedService, action, status: "running" });
    if (action === "stop") setServiceStatusModal({ visible: true, data: selectedService, action, status: null });
  };

  const handleServiceEndSubmit = async (servicePayload) => {
    try {
      if (!servicePayload) return;
      if (!selectedService) return;

      // Capture the final position while the watcher is still alive. This awaits the
      // capture attempt (the tracker applies its own controlled timeout) and never throws,
      // so a GPS failure does not block ending the service.
      await locationTracker.captureActionLocation({
        service_id_raw: selectedService.service_id_raw,
        equipment_id: selectedService.equipment?.[0]?._id || null,
        action: LOCATION_ACTIONS.SERVICE_END,
      });

      const payload = {
        _id: selectedService._id,
        embarked_count: selectedService.embarked_count,
        start_date_effective: selectedService.start_date_effective,
        end_date_effective: servicePayload.end_date_effective,
        observations: servicePayload.observations || undefined,
      };

      const previousMovementsResponse = await getMovementsFromStoreByContext({
        user_id: auth?.data?._id || null,
        service_id_raw: selectedService.service_id_raw,
        equipment: selectedService.equipment?.[0]?._id || null,
      });
      if (previousMovementsResponse.status === false) {
        await apiClient.post("/error-capture", {
          error: previousMovementsResponse.data,
          action: "/Services/handleServiceEndSubmit/ getMovementsFromStore",
          timestamp: new Date().toISOString(),
        });
      }
      if (previousMovementsResponse.status === true) {
        const movementsPayload = previousMovementsResponse?.data?.data || {};
        if (isNilEmptyOrEmptyObject(movementsPayload) === false) {
          const movementsResponse = await apiClient.post("/goods/consumptions-sales", movementsPayload);

          if (!movementsResponse.status) {
            if (isMissingTokenQueueError(movementsResponse)) {
              showMissingTokenQueueNotification();
              return;
            }
            notification.error({
              message: "Erro",
              description: "Erro ao exportar movimentos",
            });
            await apiClient.post("/error-capture", {
              error: movementsResponse.data,
              action: "/Services/handleServiceEndSubmit/ post /goods/consumptions-sales",
              timestamp: new Date().toISOString(),
            });
            return;
          }
          const key = `${movementsPayload.equipment}_${dayjs().format("DD-MM-YYYY")}`;
          const moneyBoxResponse = await updateMoneyBox(key, movementsPayload.moneyBoxTotal || 0);
          if (moneyBoxResponse.status === false) {
            await apiClient.post("/error-capture", {
              error: moneyBoxResponse.data,
              action: "/Services/handleServiceEndSubmit/ updateMoneyBox",
              timestamp: new Date().toISOString(),
            });
          }

          // update stocks when submitting movements
          const stocksToUpdateResponse = await getStoredNewStocksByContext({
            user_id: auth?.data?._id || null,
            service_id_raw: selectedService.service_id_raw,
            equipment: selectedService.equipment?.[0]?._id || null,
          });
          if (stocksToUpdateResponse.status && stocksToUpdateResponse.data.length > 0) {
            const response = await updateStocksStore(stocksToUpdateResponse.data);
            if (response.status === false) {
              await apiClient.post("/error-capture", {
                error: response.data,
                action: "/Services/handleServiceEndSubmit/ updateStocksStore",
                timestamp: new Date().toISOString(),
              });
            }
          }
        }
      }
      // send counted stocks to backend
      const countedStocksResponse = await getCountedStocksFromStoreByContext({
        user_id: auth?.data?._id || null,
        service_id_raw: selectedService.service_id_raw,
        equipment: selectedService.equipment?.[0]?._id || null,
      });
      if (countedStocksResponse.status === false) {
        await apiClient.post("/error-capture", {
          error: countedStocksResponse.data,
          action: "/Services/handleServiceEndSubmit/ getOffline /goods/counted-stocks",
          timestamp: new Date().toISOString(),
        });
      }
      if (countedStocksResponse.status === true && countedStocksResponse.data.length > 0) {
        for (const countedStock of countedStocksResponse.data) {
          countedStock.service_start_date = selectedService.final_start_date;
          const sendIT = await apiClient.post("/goods/counted-stocks", countedStock);

          if (sendIT.status === false) {
            if (isMissingTokenQueueError(sendIT)) {
              showMissingTokenQueueNotification();
              return;
            }
            notification.error({
              message: "Erro",
              description: "Erro ao enviar movimentos",
            });
            await apiClient.post("/error-capture", {
              error: sendIT.data,
              action: "/Services/handleServiceEndSubmit/ post /goods/counted-stocks",
              timestamp: new Date().toISOString(),
            });
          } else {
            await deleteCountedStocksFromStoreByContext({
              user_id: auth?.data?._id || null,
              service_id_raw: selectedService.service_id_raw,
              equipment: selectedService.equipment?.[0]?._id || null,
            });
          }
        }
      }

      // run terminateService only if offline to update local table
      terminateService(selectedService.service_id_raw, payload);
      const response = await apiClient.post("/services/end", payload);
      if (!response.status) {
        if (isMissingTokenQueueError(response)) {
          showMissingTokenQueueNotification();
          return;
        }
        notification.error({
          message: "Erro",
          description: "Erro ao terminar serviço",
        });
        await apiClient.post("/error-capture", {
          error: response.data,
          action: "/Services/handleStartStopService/ post /services/end",
          timestamp: new Date().toISOString(),
        });
        return;
      }

      // Send info capture to the server for debugging purposes
      await apiClient.post("/info-capture", {
        extraData: {
          location: "src/views/app-views/services/index.js/handleServiceEndSubmit",
          timestamp: new Date().toISOString(),
        },
        info: {
          location: "src/views/app-views/services/index.js/handleServiceEndSubmit",
          timestamp: new Date().toISOString(),
          service_id: selectedService?.service_id_raw || null,
          equipment: selectedService?.equipment?.[0]?._id || null,
          payload: payload,
        },
        subject: "Boats: Mobile Service",
        message: `Boats: Service ended (${auth?.data?.name || "Unknown User"})`,
      });

      await deleteMovementsFromStoreByContext({
        user_id: auth?.data?._id || null,
        service_id_raw: selectedService.service_id_raw,
        equipment: selectedService.equipment?.[0]?._id || null,
      });

      // Service ended successfully: stop the watcher (final position already captured above).
      locationTracker.stopTracking();

      await clearSelectedService();
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Erro ao terminar serviço",
      });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/Services/handleServiceEndSubmit/catch",
        timestamp: new Date().toISOString(),
      });
    }
  };

  const EquipmentItem = ({ item, textColor }) => {
    if (item.equipment?.length === 0) {
      return (
        <Text style={{ fontSize: 16, color: "#711515" }}>
          <small>
            <em>Sem equipamento associado</em>
          </small>
        </Text>
      );
    }
    return <Text style={{ fontSize: 16, color: textColor }}>{item.equipment.map((e) => e.name).join(", ")}</Text>;
  };
  const StaffItem = ({ item, textColor }) => {
    if (item.staff?.length === 0) {
      return null;
    }
    return <Text style={{ fontSize: 16, color: textColor }}>{item.staff.map((s) => cropStaffName(s.name)).join(", ")}</Text>;
  };

  const renderServicesList = () => {
    if (loading) {
      return (
        <Card style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text style={{ color: "#8c8c8c" }}>A carregar serviços...</Text>
          </div>
        </Card>
      );
    }

    if (data.length === 0) {
      return (
        <Card>
          <Empty description="Nenhum serviço disponível no momento" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      );
    }

    return (
      <Space orientation="vertical" size={16} style={{ width: "100%" }}>
        {data.map((item) => {
          const disabled =
            selectedService?.serviceStatus === "running" ||
            selectedService?.serviceStatus === "completed" ||
            selectedService?.end_date_effective ||
            item.status === "completed" ||
            item.equipment?.length === 0;

          const backgroundColor = disabled ? "#ededed" : selectedService?._id === item._id ? token.selectedColor : "#ffffff";
          const borderColor = disabled ? "#e8e8e8" : selectedService?._id === item._id ? token.selectedColor : "#e8e8e8";
          const textColor = disabled ? "#8c8c8c" : selectedService?._id === item._id ? "#ffffff" : "#262626";
          return (
            <Button
              key={item._id}
              size="large"
              block
              type={selectedService?._id === item._id ? "primary" : "default"}
              disabled={disabled}
              className="md-services-service-select-btn"
              style={{
                backgroundColor: backgroundColor,
                border: `2px solid ${borderColor}`,
              }}
              onClick={() => handleServiceSelect(item)}
            >
              <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 18, color: textColor }}>
                  {item.product?.name || "Serviço"}
                </Text>
                <Space size={16} wrap>
                  <Text style={{ fontSize: 14, color: textColor }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {moment(item.final_start_date).format("HH:mm")} - {moment(item.final_end_date).format("HH:mm")}
                  </Text>
                  <EquipmentItem item={item} textColor={textColor} />
                  <StaffItem item={item} textColor={textColor} />
                </Space>
                {item.entries?.map((entry) => {
                  if (entry.product.product_for_service !== null) {
                    return (
                      <>
                        <Text style={{ fontSize: 16, fontWeight: 600, color: textColor }}>
                          {selectedService?._id === item._id && <WarningFilled style={{ color: "#ff4d4f", marginRight: 4 }} />}
                          Desagua: {entry.product.name} PAX: {entry.passengersList?.map((p) => p.pax_i + p.pax_m + p.pax_z)}
                        </Text>
                      </>
                    );
                  } else {
                    return null;
                  }
                })}
              </Space>
            </Button>
          );
        })}
      </Space>
    );
  };

  return (
    <PageLayoutWrapper title="Seleção de Serviço">
      {selectedService && (
        <Card className="md-services-selected-service-card" styles={{ body: { padding: 5 } }}>
          {/* Line 1: Main Service Info */}
          <Space orientation="vertical" size={12} style={{ width: "100%" }}>
            <SelectedServiceInfoCard selectedService={selectedService} />
            <Divider style={{ margin: 0 }} />
            {/* Line 2: Embarque/Desembarque */}
            <SelectedServiceActionsCard
              selectedService={selectedService}
              form={form}
              setEmbarkationModal={setEmbarkationModal}
              handleStartStopService={handleStartStopService}
            />
          </Space>
        </Card>
      )}

      {/* Services List */}
      {renderServicesList()}

      <StartStopService
        visible={serviceStatusModal.visible}
        data={serviceStatusModal.data}
        action={serviceStatusModal.action}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
        onClose={() => {
          setServiceStatusModal({ visible: false });
        }}
        onConfirm={(payload) => {
          handleServiceEndSubmit(payload);
          setServiceStatusModal({ visible: false });
        }}
      />
      <Modal
        title="Contagem de Passageiros"
        open={embarkationModal}
        okButtonProps={{ size: "large" }}
        cancelButtonProps={{ size: "large" }}
        okText="Confirmar"
        cancelText="Cancelar"
        onOk={() => {
          setSelectedService(
            {
              ...selectedService,
              embarked_count: form.getFieldValue("embarked_count"),
            },
            auth?.data?._id || null,
          );
          setEmbarkationModal(false);
        }}
        afterOpenChange={(open) => {
          if (open && inputRef.current) {
            setTimeout(() => {
              inputRef.current.focus();
            }, 50);
          }
        }}
        onCancel={() => {
          form.resetFields();
          setEmbarkationModal(false);
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item label="Nº de Pessoas a Bordo" name="embarked_count" rules={[{ required: true, message: "Insira o número  total de passageiros" }]}>
            {/*
           <InputNumber ref={inputRef} prefix={<UserOutlined />} min={0} size="large" style={{ width: "100%" }} placeholder="0" /> */}
            <InputMobileNumber inputRef={inputRef} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Seleciona um Barco"
        open={equipmentModal.visible}
        okButtonProps={{ size: "large" }}
        cancelButtonProps={{ size: "large" }}
        onOk={async () => {
          try {
            const values = await equipmentForm.validateFields();
            const selectedEquipment = equipmentModal?.equipments.find((e) => e._id === values.equipment);

            setSelectedService({ ...selectedService, equipment: [selectedEquipment] }, auth?.data?._id || null);

            deleteMovementsFromStoreByContext({
              user_id: auth?.data?._id || null,
              service_id_raw: selectedService?.service_id_raw || null,
              equipment: selectedEquipment?._id || null,
            });
            deleteCountedStocksFromStoreByContext({
              user_id: auth?.data?._id || null,
              service_id_raw: selectedService?.service_id_raw || null,
              equipment: selectedEquipment?._id || null,
            });

            setEquipmentModal({ visible: false, equipments: null });
            equipmentForm.resetFields();
          } catch (err) {}
        }}
        onCancel={() => {
          clearSelectedService();
          setEquipmentModal({ visible: false, equipments: null });
        }}
      >
        <Form form={equipmentForm} layout="vertical" preserve={false}>
          <Form.Item name="equipment" rules={[{ required: true, message: "Seleciona um Barco" }]}>
            <div className="md-services-equipment-options">
              <Form.Item shouldUpdate noStyle>
                {() => {
                  const selectedId = equipmentForm.getFieldValue("equipment");

                  return equipmentModal?.equipments?.map((item) => (
                    <Button
                      key={item._id}
                      size="large"
                      type={item._id === selectedId ? "primary" : "default"}
                      className="md-services-equipment-option-btn"
                      onClick={() => equipmentForm.setFieldsValue({ equipment: item._id })}
                    >
                      {item.name}
                    </Button>
                  ));
                }}
              </Form.Item>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </PageLayoutWrapper>
  );
};

export default Services;
